import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router'
import { z } from 'zod'

import {
  adminCreateFamilyMutation,
  adminListFamiliesOptions,
  adminListFamiliesQueryKey,
} from '@/client/@tanstack/react-query.gen'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

const familySchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
})
type FamilyValues = z.infer<typeof familySchema>

function CreateFamilyDialog() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const form = useForm<FamilyValues>({
    resolver: zodResolver(familySchema),
    defaultValues: { name: '' },
  })
  const create = useMutation({
    ...adminCreateFamilyMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: adminListFamiliesQueryKey() })
      setOpen(false)
      form.reset()
    },
  })

  const onSubmit = (values: FamilyValues): void => {
    create.mutate({ body: values })
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>New family</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New family</DialogTitle>
          <DialogDescription>Create a family to enroll parents and devices.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="The Smiths" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="submit" disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export function AdminFamilies() {
  const families = useQuery(adminListFamiliesOptions())

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Families</h1>
        <CreateFamilyDialog />
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Parents</TableHead>
            <TableHead>Devices</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {families.data?.map((family) => (
            <TableRow key={family.id}>
              <TableCell className="font-medium">
                <Link to={`/admin/families/${family.id}`} className="hover:underline">
                  {family.name}
                </Link>
              </TableCell>
              <TableCell className="capitalize">{family.subscription_status}</TableCell>
              <TableCell>{family.parent_count}</TableCell>
              <TableCell>{family.device_count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {families.isSuccess && families.data.length === 0 && (
        <p className="text-muted-foreground">No families yet.</p>
      )}
    </div>
  )
}
